import { useCallback, useMemo, useRef } from 'react';
import { Color, Element, ElementSelectEventArgs } from '@thive/canvas';
import { Flow } from '@thive/fddm-types';

import {
  intersect,
  isIntersect,
  primitiveArraysHaveEqualContents,
} from '@/modules/common/helpers/array';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';
import { getFlowLineColor, groupFlowsByType } from '@/modules/flowsV2/helpers';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { determinePanelMode } from '@/modules/app/helpers';
import { useCanvasStore } from '../store';

const isMovableSelectionElement = (element: Element): boolean =>
  Boolean(element.interactivity?.selectable && element.interactivity?.draggable);

/** @description Filters out non-movable elements like flows */
const getEffectiveSelection = (elements: readonly Element[]): Element[] =>
  elements.length > 1 ? elements.filter(isMovableSelectionElement) : [...elements];

function blurActiveInput() {
  const { activeElement } = document;

  if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
    activeElement.blur();
  }
}

export const useSelectedElement = () => {
  const getRelatedFlows = useFloorplanDesignStore((state) => state.getRelatedFlows);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const isSyncingFilteredSelection = useRef(false);
  const isHandlingSelection = useRef(false);

  const setElementsStrokeColor = useCallback((ids: string[], color: string) => {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    canvas.updateElements(
      canvas.getElements(ids).map((element) => ({
        ...element,
        stroke: Color.fromHex(color),
      })),
    );
  }, []);

  const singularStationAreaSelected = useCallback(
    (element: Element) => {
      groupFlowsByType(getRelatedFlows([element.id])).forEach(
        (value: string[], key: Flow['type']) => {
          setElementsStrokeColor(value, modifyColor(getFlowLineColor(key)));
        },
      );
    },
    [getRelatedFlows, setElementsStrokeColor],
  );

  const clearOldSelection = useCallback(
    (newlySelectedElements: string[]) => {
      const { selectedIds } = useAppStore.getState();
      const elementsToKeepSelected = intersect(selectedIds, newlySelectedElements);
      const elementsToClearFromSelection = selectedIds
        .filter((item) => !elementsToKeepSelected.includes(item))
        .map((item) => item);

      if (elementsToClearFromSelection.length === 0) return;

      // const tracksToClear = useFloorplanDesignStore
      //   .getState()
      //   .getTracks(elementsToClearFromSelection);
      // const trackPiecesToClear: string[] = [];
      // tracksToClear.forEach((track) => {
      //   getTrackIds(track).forEach((id) => trackPiecesToClear.push(id));
      // });

      const flowsToClear = getRelatedFlows(elementsToClearFromSelection);

      // setElementsStrokeColor(trackPiecesToClear, TRACK_PIECE_COLOR);
      groupFlowsByType(flowsToClear).forEach((value: string[], key: Flow['type']) => {
        setElementsStrokeColor(value, modifyColor(getFlowLineColor(key), false, '80'));
      });
    },
    [getRelatedFlows, setElementsStrokeColor],
  );

  const applyEffectiveSelectionToCanvas = useCallback((ids: string[]) => {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas || isSyncingFilteredSelection.current) return;

    isSyncingFilteredSelection.current = true;

    try {
      if (ids.length === 0) {
        canvas.deselectAll();
        return;
      }

      canvas.setSelection(ids);
    } finally {
      isSyncingFilteredSelection.current = false;
    }
  }, []);

  const elementSelected = useCallback(
    (event: ElementSelectEventArgs) => {
      if (isSyncingFilteredSelection.current || isHandlingSelection.current) return;

      isHandlingSelection.current = true;

      try {
        const elements = getEffectiveSelection(event.elements);
        const ids = elements.map(({ id }) => id);
        const selectionWasFiltered = elements.length !== event.elements.length;
        const { selectedIds } = useAppStore.getState();

        if (elements.length === 0 && selectedIds.length > 0) {
          appSvc.resetSelection();
        }

        /*
         * The canvas does not necessarily take DOM focus away from a property input.
         * Commit the active input before changing selectedIds so its draft is applied
         * to the entity that was being edited.
         */
        blurActiveInput();

        if (primitiveArraysHaveEqualContents(ids, selectedIds)) {
          if (selectionWasFiltered) {
            applyEffectiveSelectionToCanvas(ids);
          }

          return;
        }

        clearOldSelection(ids);

        if (elements.length === 0) {
          if (selectionWasFiltered) {
            applyEffectiveSelectionToCanvas(ids);
          }

          return;
        }

        if (elements.length === 1) {
          const [element] = elements;

          if (
            element.tags &&
            isIntersect(element.tags, [FDDM_TAG.SINGULAR_STATION_AREA, FDDM_TAG.RACKING_AREA])
          ) {
            singularStationAreaSelected(element);
          }
        }

        const fddmStore = useFloorplanDesignStore.getState();
        const areas = fddmStore.getAreasByIds(ids);
        const templates = fddmStore.getTemplatesByIds(ids);

        appSvc.setPanelMode(
          determinePanelMode(
            fddmStore.getObstaclesByIds(ids),
            areasToFlatCollection(areas),
            templates,
            fddmStore.getGroupsByIds(ids),
            fddmStore.getLogicalSpacesByIds(ids),
          ),
        );
        appSvc.setSelectedIds(ids);

        if (selectionWasFiltered) {
          applyEffectiveSelectionToCanvas(ids);
        }
      } finally {
        isHandlingSelection.current = false;
      }
    },
    [appSvc, applyEffectiveSelectionToCanvas, clearOldSelection, singularStationAreaSelected],
  );

  return { elementSelected };
};
